import type { ExportData } from '../../../../map-editor-entities/schema/exportSchema'

/**
 * Export 직전 데이터 무결성 검증.
 *
 * zod 스키마(ExportDataSchema)는 필드의 형태만 본다. 여기서는 형태는 맞지만
 * 현장에서 사고가 나는 값을 잡는다:
 *   1. IP 중복      — 같은 IP 를 두 장비가 쓰면 한쪽이 통신 불가
 *   2. CCTV 이름 중복 — 라우터가 객체를 name 으로 키잉하고 관계 참조도 name 기반이라,
 *                      이름이 겹치면 관계가 엉뚱한 CCTV 에 붙는다
 *   3. CCTV IP 누락  — 비어 있거나 0.0.0.0 인 CCTV 는 조명서버가 접속할 주소가 없다
 *   4. IP 형식 오류  — 172.1931.16 같은 오타(옥텟 수/범위)는 장비 접속이 안 된다
 *   5. CCTV 주차면 관계 — 조명 CCTV 는 최소 1개, 단방향 CCTV 는 연결 금지
 *   6. 비상벨 CCTV 관계 — 같은 층의 조명 CCTV 에 연결되어야 한다
 *
 * 검사 단위는 전 층 통합이다. IP 와 CCTV 이름은 층이 아니라 현장 전체에서 유일해야 한다.
 */

/** 미입력 IP. 0.0.0.0 은 transformer 가 넣는 기본값이라 중복으로 보지 않는다. */
const EMPTY_IPS = new Set(['', '0.0.0.0'])

/** IPv4 형식: 옥텟 4개, 각 0~255. (172.1931.16 같은 오타를 잡는다) */
function isValidIpv4(ip: string): boolean {
  const parts = ip.split('.')
  if (parts.length !== 4) return false
  return parts.every((p) => /^\d{1,3}$/.test(p) && Number(p) <= 255)
}

/** 메시지 한 줄에 나열할 최대 항목 수 (ExportModal 의 zod 오류 표시와 동일한 상한) */
const MAX_LISTED = 10

interface ObjectRef {
  floor: string
  label: string
}

function refToString(ref: ObjectRef): string {
  return `${ref.floor}/${ref.label}`
}

function joinRefs(refs: string[]): string {
  if (refs.length <= MAX_LISTED) return refs.join(', ')
  return `${refs.slice(0, MAX_LISTED).join(', ')} ... 외 ${refs.length - MAX_LISTED}건`
}

function addTo(map: Map<string, ObjectRef[]>, key: string, ref: ObjectRef): void {
  const owners = map.get(key)
  if (owners) owners.push(ref)
  else map.set(key, [ref])
}

function relationNames(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .filter((name): name is string => typeof name === 'string')
    .map((name) => name.trim())
    .filter(Boolean)
}

/**
 * mapData 한 층을 (카테고리, 객체배열) 쌍으로 펼친다.
 * cctvs 만 배열이 아니라 { lightCctvs, warningCctvs } 객체이고, 나머지는 배열이다
 * (사용자 정의 레이어 키도 배열이라 함께 순회된다).
 */
function flattenCategories(mapData: Record<string, unknown>): Array<[string, any[]]> {
  const groups: Array<[string, any[]]> = []

  for (const [key, value] of Object.entries(mapData)) {
    if (key === 'cctvs') {
      const cctvs = value as { lightCctvs?: any[]; warningCctvs?: any[] } | undefined
      groups.push(['lightCctvs', cctvs?.lightCctvs ?? []])
      groups.push(['warningCctvs', cctvs?.warningCctvs ?? []])
    } else if (Array.isArray(value)) {
      groups.push([key, value])
    }
  }

  return groups
}

/**
 * Export 데이터를 검증해 오류 메시지 목록을 돌려준다.
 * 빈 배열이면 통과.
 */
export function validateMapData(data: ExportData): string[] {
  const ipOwners = new Map<string, ObjectRef[]>()
  const cctvNameOwners = new Map<string, ObjectRef[]>()
  const cctvWithoutIp: ObjectRef[] = []
  const malformedIps: string[] = []
  const cctvWithoutParking: ObjectRef[] = []
  const warningCctvWithParking: ObjectRef[] = []
  const emergencyBellWithoutCctv: string[] = []

  for (const level of data.data.parkingLotLevels) {
    const floor = level.name
    const mapData = level.mapData as unknown as Record<string, unknown>
    if (!mapData) continue

    // 관계는 이름으로 내보낸다. 같은 층의 선택된 객체만 연결 대상으로 인정한다.
    const parkingLocations = level.mapData.parkingLocations ?? []
    const parkingNames = new Set(parkingLocations.map((object) => object.name?.trim()).filter(Boolean))
    const lightCctvNames = new Set(
      (level.mapData.cctvs?.lightCctvs ?? []).map((object) => object.name?.trim()).filter(Boolean)
    )
    const cctvsReferencedByParking = new Set(
      parkingLocations.flatMap((object) => relationNames(object.monitoringCctvs))
    )

    for (const [category, objects] of flattenCategories(mapData)) {
      for (const object of objects) {
        const name = typeof object?.name === 'string' ? object.name.trim() : ''
        const ref: ObjectRef = { floor, label: name || `(이름없음 id=${object?.id})` }

        const ip = typeof object?.ipAddress === 'string' ? object.ipAddress.trim() : ''
        if (!EMPTY_IPS.has(ip)) {
          addTo(ipOwners, ip, ref)
          if (!isValidIpv4(ip)) malformedIps.push(`${refToString(ref)} → ${ip}`)
        }

        const isCctv = category === 'lightCctvs' || category === 'warningCctvs'
        if (isCctv && name) addTo(cctvNameOwners, name, ref)
        if (isCctv && EMPTY_IPS.has(ip)) cctvWithoutIp.push(ref)

        const parkingRelations = relationNames(object?.linkedParkingLocationIds)
        if (category === 'lightCctvs' && !parkingRelations.some((target) => parkingNames.has(target))) {
          cctvWithoutParking.push(ref)
        }
        if (category === 'warningCctvs' && (parkingRelations.length > 0 || cctvsReferencedByParking.has(name))) {
          warningCctvWithParking.push(ref)
        }
        if (category === 'emergencyBells') {
          const target = typeof object?.linkedCctvId === 'string' ? object.linkedCctvId.trim() : ''
          if (!target || !lightCctvNames.has(target)) {
            emergencyBellWithoutCctv.push(`${refToString(ref)} → ${target || '(연결 없음)'}`)
          }
        }
      }
    }
  }

  const issues: string[] = []

  for (const [ip, owners] of ipOwners) {
    if (owners.length > 1) {
      issues.push(`IP 중복 (${owners.length}개): ${ip} — ${joinRefs(owners.map(refToString))}`)
    }
  }

  for (const [name, owners] of cctvNameOwners) {
    if (owners.length > 1) {
      issues.push(`CCTV 이름 중복 (${owners.length}개): ${name} — ${joinRefs(owners.map((o) => o.floor))}`)
    }
  }

  if (malformedIps.length > 0) {
    issues.push(`IP 형식 오류 ${malformedIps.length}건: ${joinRefs(malformedIps)}`)
  }

  if (cctvWithoutIp.length > 0) {
    issues.push(
      `IP 가 없는 CCTV ${cctvWithoutIp.length}대 (빈 값 또는 0.0.0.0): ` + joinRefs(cctvWithoutIp.map(refToString))
    )
  }

  if (cctvWithoutParking.length > 0) {
    issues.push(
      `연결된 주차구역이 0개인 전방위(조명) CCTV ${cctvWithoutParking.length}대: ` +
        joinRefs(cctvWithoutParking.map(refToString)) +
        ' — 같은 층에서 내보내는 주차구역을 1개 이상 연결해주세요.'
    )
  }

  if (warningCctvWithParking.length > 0) {
    issues.push(
      `주차구역에 연결된 단방향 CCTV ${warningCctvWithParking.length}대: ` +
        joinRefs(warningCctvWithParking.map(refToString)) +
        ' — 단방향 CCTV의 주차구역 연결을 해제해주세요.'
    )
  }

  if (emergencyBellWithoutCctv.length > 0) {
    issues.push(
      `CCTV 연결 오류가 있는 비상벨 ${emergencyBellWithoutCctv.length}개: ` +
        joinRefs(emergencyBellWithoutCctv) +
        ' — 같은 층에서 내보내는 전방위(조명) CCTV에 연결해주세요.'
    )
  }

  return issues
}
